/**
 * Contacts. A table, a search, and the one column that matters legally: consent.
 *
 * Phone numbers here go through phoneFor(), so an ops user sees the contact hash instead —
 * visibly, with the reason next to it, rather than a blank cell that looks like missing data.
 *
 * Author: Danish Khan
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState, type ReactNode } from 'react';
import { api } from '../lib/api.ts';
import { customerTypeLabel, formatAgo, LANG_LABEL } from '../lib/format.ts';
import { useOpenLead } from '../lib/openLead.ts';
import { canSeePhone, phoneFor } from '../lib/pii.ts';
import { useUser } from '../lib/session.tsx';
import type { Contact } from '../lib/types.ts';
import { Button } from '../components/Button.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import { Pill, TierPill } from '../components/Pill.tsx';
import { useToast } from '../components/Toast.tsx';

const column = createColumnHelper<Contact>();

export function ContactsPage(): ReactNode {
  const user = useUser();
  const { toastError } = useToast();
  const openLead = useOpenLead((err) => toastError('open that contact’s thread', err));
  const [q, setQ] = useState('');
  const [consent, setConsent] = useState<'all' | 'opted_out'>('all');

  /**
   * Paged, because `GET /contacts` pages: a hundred rows at a time, keyset on the contact id.
   *
   * The id IS the cursor — ULIDs sort by creation — so "the next hundred" stays well defined while
   * new contacts are being written at the other end, which is the failure an OFFSET would have.
   * At sixteen contacts nothing is truncated; at five thousand, a table that silently showed the
   * first hundred and called it "Contacts" would be a number people quote.
   */
  const contacts = useInfiniteQuery({
    queryKey: ['contacts', q, consent],
    queryFn: ({ pageParam }) =>
      api.contacts({ q, consent, ...(pageParam === null ? {} : { cursor: pageParam }) }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = useMemo(() => contacts.data?.pages.flatMap((p) => p.rows) ?? [], [contacts.data]);

  const columns = useMemo(
    () => [
      column.accessor('displayName', {
        header: 'Name',
        cell: (ctx) => <span className="truncate text-sm font-medium text-fg">{ctx.getValue()}</span>,
      }),
      column.accessor('phone', {
        header: 'WhatsApp',
        cell: (ctx) => {
          const p = phoneFor(user.role, ctx.row.original);
          return (
            <span className={p.redacted ? 'font-mono text-[11px] text-fg-subtle' : 'text-xs text-fg-muted'} title={p.redacted ? 'Phone numbers are visible to roles rep and above' : undefined}>
              {p.text}
            </span>
          );
        },
      }),
      column.accessor('city', {
        header: 'City',
        meta: { width: '120px' },
        cell: (ctx) => <span className="text-xs text-fg-muted">{ctx.getValue() ?? '—'}</span>,
      }),
      column.accessor('lang', {
        header: 'Language',
        meta: { width: '110px' },
        cell: (ctx) => <Pill tone="neutral">{LANG_LABEL[ctx.getValue()] ?? ctx.getValue()}</Pill>,
      }),
      column.accessor('customerType', {
        header: 'Type',
        meta: { width: '120px' },
        // The API defaults this to "unknown" when nobody has classified the contact, which is most
        // of them; the column printed that word verbatim.
        cell: (ctx) => (
          <Pill tone={ctx.getValue() === 'b2b' ? 'info' : 'neutral'}>{customerTypeLabel(ctx.getValue())}</Pill>
        ),
      }),
      column.accessor('tier', {
        header: 'Tier',
        meta: { width: '90px' },
        cell: (ctx) => <TierPill tier={ctx.getValue()} />,
      }),
      column.accessor('optedOut', {
        header: 'Consent',
        meta: { width: '120px' },
        cell: (ctx) =>
          ctx.getValue() ? <Pill tone="danger">opted out</Pill> : <Pill tone="ok">reachable</Pill>,
      }),
      column.accessor('lastSeenAt', {
        header: 'Last heard',
        meta: { width: '120px' },
        cell: (ctx) => <span className="text-xs text-fg-muted">{formatAgo(ctx.getValue())}</span>,
      }),
    ],
    [user.role],
  );

  if (contacts.isError) {
    return <ErrorState error={contacts.error} context="load contacts" onRetry={() => void contacts.refetch()} />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2.5">
        {/*
          Name and number, and NOT city: `routes/contacts.ts` searches `display_name`,
          `profile_name` and — for rep and above — `wa_id`. The placeholder used to offer a city
          search, so typing "Udaipur" returned nothing and read as a broken search rather than as a
          filter this route does not have. The City column stays; the server just never matches on it.
        */}
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={canSeePhone(user.role) ? 'Search a name or a number…' : 'Search a name…'}
          aria-label="Search contacts"
          className="h-8 w-64 rounded-[var(--h1-radius)] border border-line-strong bg-bg px-2.5 text-sm text-fg"
        />
        <select
          value={consent}
          onChange={(e) => setConsent(e.target.value as 'all' | 'opted_out')}
          aria-label="Filter by consent"
          className="h-8 rounded-[var(--h1-radius)] border border-line-strong bg-bg px-2 text-xs text-fg"
        >
          <option value="all">All contacts</option>
          <option value="opted_out">Opted out only</option>
        </select>
        <p className="ml-auto text-[11px] text-fg-subtle">
          {canSeePhone(user.role)
            ? 'Numbers visible to your role — exports are audited.'
            : 'Your role sees contact hashes, not numbers — and search does not match on a number either, because finding a row by typing one would confirm it.'}
        </p>
      </div>
      <div className="min-h-0 flex-1">
        <DataTable
          data={rows}
          loading={contacts.isLoading}
          columns={columns}
          rowKey={(c) => c.id}
          ariaLabel="Contacts"
          onRowActivate={(c) => c.leadId && openLead(c.leadId)}
          emptyTitle="No contacts match"
          emptyBody="Try a shorter search."
        />
      </div>
      {/*
        The count says what is on screen and whether that is all of it. "16 contacts" when there
        are five thousand is the kind of number somebody repeats in a meeting.
      */}
      <div className="flex shrink-0 items-center gap-3 border-t border-line bg-surface px-4 py-2">
        <p className="text-[11px] text-fg-subtle">
          {rows.length} contact{rows.length === 1 ? '' : 's'}
          {contacts.hasNextPage ? ' shown — there are more' : ' — that is all of them'}
        </p>
        {contacts.hasNextPage && (
          <Button
            variant="secondary"
            size="sm"
            disabled={contacts.isFetchingNextPage}
            onClick={() => void contacts.fetchNextPage()}
          >
            {contacts.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        )}
      </div>
    </div>
  );
}
